// src/utils/sessionManager.ts

import { supabase, checkSessionError } from './supabaseClient';

/**
 * Check if the current session is valid and refresh if needed
 * @returns The current valid session or null if no session exists
 */
export const ensureValidSession = async () => {
    try {
        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
            checkSessionError(sessionError);
            return null;
        }

        if (!session) {
            return null;
        }

        // Check if session is about to expire (within 5 minutes)
        const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;

        if (expiresAt - now < fiveMinutes) {
            // Session is about to expire or already expired, try to refresh
            const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();

            if (refreshError) {
                checkSessionError(refreshError);
                return null;
            }

            return refreshedSession;
        }

        return session;
    } catch (error) {
        console.error('Error ensuring valid session:', error);
        if (error instanceof Error) {
            checkSessionError(error);
        }
        return null;
    }
};

/**
 * Setup automatic session refresh
 * This will check and refresh the session every 4 minutes
 */
export const setupAutoSessionRefresh = () => {
    if (typeof window === 'undefined') {
        return;
    }

    // Check and refresh session every 4 minutes
    const intervalId = setInterval(async () => {
        try {
            const session = await ensureValidSession();
            if (!session) {
                // No valid session, clear the interval
                clearInterval(intervalId);
            }
        } catch (error) {
            console.error('Auto session refresh error:', error);
        }
    }, 4 * 60 * 1000); // 4 minutes

    // Return cleanup function
    return () => clearInterval(intervalId);
};

/**
 * Get the current session without refreshing
 * Use this for quick checks where you don't need to refresh
 */
export const getCurrentSession = async () => {
    try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
            checkSessionError(error);
            return null;
        }

        return session;
    } catch (error) {
        console.error('Error getting current session:', error);
        return null;
    }
};

/**
 * Check if the current session is expired
 */
export const isSessionExpired = async (): Promise<boolean> => {
    const session = await getCurrentSession();

    if (!session || !session.expires_at) {
        return true;
    }

    const expiresAt = session.expires_at * 1000;
    const now = Date.now();

    return now >= expiresAt;
};
